'use client'
import { H1, ColorButton, Table, TableRow, TableHeader, CheckboxInput, TableCell } from "@/components/ui";
import { File, Folder } from "@/assets/icons";
import { useFTP } from "@/hooks/useFTP";
import path from "path-browserify";
import { useSearchParams } from "next/navigation";
import prettyBytes from "pretty-bytes";
import { useEffect } from "react";
import { RouteType } from "@/types";

interface ClientItemsProps {
    routeType?: RouteType;
}

export function ClientItems({ routeType = 'ACCOUNTANT' }: ClientItemsProps) {
    const searchParams = useSearchParams();
    const client = searchParams.get('client');

    const { currentPath, contents, isLoading, isDownloading, loadDirectory, error, selectedItems, downloadSelected,
        toggleAllItems, toggleItem } = useFTP(routeType);

    useEffect(() => {
        const clientParam = client || undefined;
        loadDirectory('', clientParam);
    }, [client, loadDirectory]);

    return (
        <div>
            <H1>Arquivos do Cliente</H1>
            <div className="flex justify-between">
                <span>Cliente: {client}</span>

                <ColorButton onClick={downloadSelected}
                    disabled={isDownloading || selectedItems.length === 0}
                >
                    {selectedItems.length === 0
                        ? 'Selecione itens para baixar'
                        : isDownloading
                            ? 'Baixando...'
                            : `Baixar ${selectedItems.length} arquivo(s)`}
                </ColorButton>

            </div>
            <div className="error-message">
                {error}
            </div>
            <div className="p-8 pt-8 hidden md:flex">
                <Table>
                    <thead>
                        <TableRow>
                            <TableHeader colSpan={3}>
                                <CheckboxInput
                                    checked={contents.length > 0 && selectedItems.length === contents.length}
                                    onChange={toggleAllItems}
                                />
                            </TableHeader>
                            <TableHeader>Arquivo</TableHeader>
                            <TableHeader>Tamanho</TableHeader>
                            <TableHeader>Modificado em</TableHeader>
                        </TableRow>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center">
                                    Carregando...
                                </TableCell>
                            </TableRow>
                        ) : contents.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center">
                                    Não existem arquivos para esse cliente.
                                </TableCell>
                            </TableRow>
                        ) : (
                            contents.map((item, index) => (
                                <TableRow key={`${item.name}-${index}`}>
                                    <TableCell>
                                        <CheckboxInput
                                            checked={selectedItems.some(i =>
                                                i.path === path.posix.join(currentPath, item.name)
                                            )}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                toggleItem(item, e as unknown as React.MouseEvent);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell colSpan={2}>
                                        {(item.type === 'directory') ? <Folder /> : <File />}
                                    </TableCell>
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell>{item.type === 'file' ? prettyBytes(item.size) : '--'}</TableCell>
                                    <TableCell>{new Date(item.modifiedAt).toLocaleDateString()}</TableCell>

                                </TableRow>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="p-8 flex md:hidden">
                <Table>
                    <thead>
                        <TableRow>
                            <TableHeader className="flex gap-10">
                                Selecionar Todos
                                <CheckboxInput
                                    checked={contents.length > 0 && selectedItems.length === contents.length}
                                    onChange={toggleAllItems}
                                />
                            </TableHeader>
                        </TableRow>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell className="text-center">
                                    Carregando...
                                </TableCell>
                            </TableRow>
                        ) : contents.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-center">
                                    Não existem arquivos para esse cliente.
                                </TableCell>
                            </TableRow>
                        ) : (
                            contents.map((item, index) => (
                                <TableRow key={`${item.name}-${index}`}>
                                    <TableCell>
                                        <CheckboxInput
                                            checked={selectedItems.some(i =>
                                                i.path === path.posix.join(currentPath, item.name)
                                            )}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                toggleItem(item, e as unknown as React.MouseEvent);
                                            }}
                                        />
                                    </TableCell>
                                    <TableHeader className={`flex gap-3`}>
                                        {(item.type === 'directory') ? <Folder /> : <File />}
                                        {(item.type === 'directory') ? 'Pasta' : 'Arquivo'}
                                    </TableHeader>
                                    <TableCell>{item.name}</TableCell>
                                    <TableHeader>Tamanho</TableHeader>
                                    <TableCell>{item.type === 'file' ? prettyBytes(item.size) : '--'}</TableCell>
                                    <TableHeader>Modificado em</TableHeader>
                                    <TableCell>{new Date(item.modifiedAt).toLocaleDateString()}</TableCell>

                                </TableRow>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    )
}